import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { VideoPhone } from "@/components/public/video-phone"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { RelatedPages } from "@/components/public/related-pages"
import { relatedFor } from "@/data/related-pages"
import type { SeoMeta } from "@/types"

export type AudienceItem = {
  title: string
  description: string
}

export type AudienceStat = {
  value: string
  label: string
  tone?: "neutral" | "accent"
}

export type AudienceFaq = { q: string; a: string }

export type AudiencePageProps = {
  meta: SeoMeta
  breadcrumbName: string
  heroEyebrow: string
  heroHeadline: string
  heroAccent?: string
  heroSubhead: string
  heroBadgeSmall?: string
  heroBadgeLarge?: string
  painsEyebrow?: string
  painsHeadline: string
  pains: AudienceItem[]
  featuresEyebrow?: string
  featuresHeadline: string
  features: AudienceItem[]
  statsHeadline: string
  stats: AudienceStat[]
  faq: AudienceFaq[]
  ctaHeadline: string
  ctaBody: string
  relatedKey?: string
}

export default function AudiencePage({
  meta,
  breadcrumbName,
  heroEyebrow,
  heroHeadline,
  heroAccent = ".",
  heroSubhead,
  heroBadgeSmall = "Reach the right buyers",
  heroBadgeLarge = "with video",
  painsEyebrow = "The challenge",
  painsHeadline,
  pains,
  featuresEyebrow = "Built for you",
  featuresHeadline,
  features,
  statsHeadline,
  stats,
  faq,
  ctaHeadline,
  ctaBody,
  relatedKey,
}: AudiencePageProps) {
  const related = relatedKey ? relatedFor(relatedKey) : []
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: meta.title,
            description: meta.description,
            url: meta.canonical,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>

        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: breadcrumbName }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                {heroEyebrow}
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                {heroHeadline}
                <span className="text-red">{heroAccent}</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">{heroSubhead}</p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative flex justify-center">
              <VideoPhone />
              <div className="absolute -bottom-4 -left-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md md:-left-2">
                <p className="text-xs font-medium text-warm-grey">{heroBadgeSmall}</p>
                <p className="font-display text-xl font-bold text-white">{heroBadgeLarge}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              {painsEyebrow}
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              {painsHeadline}
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {pains.map((item, i) => (
              <ScrollReveal key={i}>
                <div className="rounded-xl border border-stone bg-white p-6">
                  <p className="font-display text-lg font-bold text-charcoal">
                    {item.title}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-slate">
                    {item.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              {featuresEyebrow}
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              {featuresHeadline}
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, i) => (
              <ScrollReveal key={i}>
                <div className="rounded-xl border border-stone bg-white p-6">
                  <p className="font-display text-base font-bold text-charcoal">
                    {feature.title}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-slate">
                    {feature.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The numbers
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-white md:text-4xl">
              {statsHeadline}
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {stats.map((stat, i) => (
              <ScrollReveal key={i}>
                <div
                  className={
                    stat.tone === "accent"
                      ? "rounded-xl border border-red/30 bg-red/10 p-6 text-center"
                      : "rounded-xl border border-white/10 bg-white/5 p-6 text-center"
                  }
                >
                  <p className="font-display text-3xl font-bold text-white">
                    {stat.value}
                  </p>
                  <p className="mt-2 text-sm text-warm-grey">{stat.label}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <dl className="divide-y divide-stone">
                {faq.map((item, i) => (
                  <details key={i} className="group">
                    <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
                      <dt className="text-sm font-semibold leading-relaxed text-charcoal">
                        {item.q}
                      </dt>
                      <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
                        <svg
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          className="size-3"
                        >
                          <path d="M8 3v10M3 8h10" />
                        </svg>
                      </span>
                    </summary>
                    <dd className="pb-5 text-sm leading-relaxed text-slate">
                      {item.a}
                    </dd>
                  </details>
                ))}
              </dl>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {related.length > 0 && <RelatedPages links={related} />}

      {/* CTA */}
      <section className="border-t border-stone px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              {ctaHeadline}
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">{ctaBody}</p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
